import React from "react";
import { ArrowRight, Sparkles } from "lucide-react";

/**
 * Before vs After Emotional Impact card.
 *
 * Deliberately minimal. Three pieces:
 *   1. A small headline: "X improvements this week"
 *   2. A row of impact indicator chips (Escalation Reduced, Softer Delivery…)
 *   3. The latest before/after pair as a single example, low-contrast.
 *
 * Strictly heuristic — no scores, no graphs, no AI calls.
 */
const BeforeAfterImpactCard = ({ impact }) => {
    if (!impact) return null;
    const { totalImprovements, indicators, latestPair } = impact;

    return (
        <section
            data-testid="impact-card"
            className="tg-enter rounded-2xl border border-border bg-card p-5 sm:p-6"
        >
            <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-full bg-[#A35741]/10 flex items-center justify-center shrink-0 ring-1 ring-[#A35741]/20">
                    <Sparkles className="h-4.5 w-4.5 text-[#A35741]" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5 font-semibold">
                        Before vs After
                    </p>
                    <p
                        data-testid="impact-headline"
                        className="font-serif text-[20px] sm:text-[22px] leading-snug text-foreground"
                    >
                        {totalImprovements === 1
                            ? "You softened one message into a clearer version."
                            : `You softened ${totalImprovements} messages into clearer, calmer versions.`}
                    </p>

                    {indicators?.length > 0 && (
                        <div
                            data-testid="impact-indicators"
                            className="mt-3 flex flex-wrap gap-1.5"
                        >
                            {indicators.map((ind) => (
                                <span
                                    key={ind.key}
                                    data-indicator={ind.key}
                                    className="inline-flex items-center gap-1.5 text-[11px] text-[#4B6143] bg-[#EAF1E8]/70 border border-[#D5E3D1] rounded-full px-2.5 py-1"
                                >
                                    <span className="h-1 w-1 rounded-full bg-[#5E7153]" />
                                    {ind.label}
                                    <span className="text-[10px] font-semibold text-[#4B6143]/70">
                                        ×{ind.count}
                                    </span>
                                </span>
                            ))}
                        </div>
                    )}

                    {latestPair && (
                        <div
                            data-testid="impact-latest-pair"
                            className="mt-4 rounded-xl bg-stone-50 border border-stone-200/80 p-3 sm:p-4"
                        >
                            <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1 font-semibold">
                                Most recent
                            </p>
                            <div className="flex flex-col sm:flex-row sm:items-stretch gap-2 sm:gap-3">
                                <div className="flex-1 min-w-0">
                                    <p className="text-[10px] uppercase tracking-[0.12em] text-stone-400 mb-1 font-semibold">
                                        {latestPair.before.label}
                                    </p>
                                    <p className="text-[13px] leading-relaxed text-stone-600 whitespace-pre-wrap break-words">
                                        {latestPair.before.text}
                                    </p>
                                </div>
                                <div className="hidden sm:flex items-center justify-center text-stone-300">
                                    <ArrowRight className="h-4 w-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-[10px] uppercase tracking-[0.12em] text-[#4B6143] mb-1 font-semibold">
                                        {latestPair.after.label}
                                    </p>
                                    <p className="text-[13px] leading-relaxed text-foreground whitespace-pre-wrap break-words">
                                        {latestPair.after.text}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default BeforeAfterImpactCard;
